import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Training & Coaching";
const heroImage = "https://net-basis.net/Bilder/ict-training-coaching-dach-market-entry.webp";
const heroAlt = "Geschäftsmann visualisiert Ziele für Vertrieb, Führung und interkulturelle Kompetenz im DACH-Markt.";
const heroClaimLine1 = "Mit Begleitung\nans Ziel!";
const benefitsTitle = "Wie Sie von unserer Expertise im Bereich __Training & Coaching__ profitieren:";
const p1Text = "Ob es sich um die allgemeine Definition der Strategie und Strukturen handelt, um ein solches Geschäft voranzutreiben und zu führen, oder um den Fokus auf klar definierte Projektbeschreibungen mit vereinbarten Meilensteinen, Kostenberechnungen und dem „Return on Investment“, oder um alle Aktivitäten rund um Vertrieb und Marketing – ein Markteintrittsprojekt kommt mit vielen wesentlichen und grundsätzlichen Themen daher, die sachgerechte Sorgfalt und Behandlung erfordern, wenn dieser Plan erfolgreich werden soll.";
const p2Text = "Noch schwieriger wird es, wenn alles für einen fremden Markt, in einer anderen Sprache, in einer anderen Umgebung und in einer anderen Kultur umgesetzt werden muss.";
const p3Text = "Wenn Sie als Hersteller trotzdem entscheiden, den Markteintritt vorrangig alleine und mit eigenem Personal zu wagen, kann Ihnen NetBasis International gleichwohl unterstützend zur Seite stehen und Sie insgesamt oder in einzelnen Bereichen mit individuellen Trainings oder Coachings begleiten. Dabei legen wir den Fokus ganz bewusst auf den Transfer von Wissen und Fertigkeiten, indem wir mit einzelnen Mitarbeitern oder ganzen Teams Ihres Unternehmens im Sinne Ihres Zieles zusammenarbeiten. Unser Anspruch an uns selbst ist es, so flexibel wie gefordert vorzugehen, herausragende Qualität zu liefern und im Laufe der Zeit zu Ihrem vertrauenswürdigen Berater zu werden.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Vorbereitende Besprechungen zur präzisen Festlegung von Trainings- und Coachingzielen",
  "Definition einer klaren Vision zur sicheren Lenkung von Entscheidungen und Maßnahmen",
  "Ganzheitliche Betrachtung aller wesentlichen Bereiche für Ihren nachhaltigen Geschäftsaufbau",
  "Fundierte Lagebeurteilung sowie strukturierte Problem- und Potenzialanalyse",
  "Strategische Begleitung bei der klaren Entwicklung Ihrer Markteintrittsstrategie",
  "Erstellung eines individuellen Aktionsplans mit messbaren Projektmanagement-Zielen",
  "Marktbezogene Analyse von Produkten, Lösungen und dem relevanten Wettbewerb",
  "Bestmögliche Produktpräsentation und wirksame Selbstpräsentation vor Entscheidern",
  "Etablierung des richtigen Modells zum Beziehungsaufbau für erfolgreiche Geschäftsabschlüsse",
  "Marktgerechte Kundenakquise, exzellenter Kundenservice und langfristige Kundenbindung",
  "Zielgerichtete Unterstützung Ihrer Mitarbeiter für eine nachweislich effektive Projektarbeit",
  "Nachhaltige Topteam-Entwicklung und wirksame Gruppen- und Teamführung",
  "Bereitstellung permanenter strategischer Unterstützung für CEO, Vorstand und Management",
  "Durchführung maßgeschneiderter Coaching-Sitzungen mit klaren Folgemaßnahmen",
];

export default function TrainingCoachingDePage() {
  const strongName = COMPANY_NAME;
  const p1Parts = p1Text.split(strongName);
  const p2Parts = p2Text.split(strongName);
  const p3Parts = p3Text.split(strongName);

  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Training & Coaching | NetBasis International"
        description="Praxisorientierte Schulungen und Coachings für Vertriebsteams zur erfolgreichen Umsetzung internationaler B2B-Strategien."
        ogUrl="https://net-basis.net/de/services/training-coaching"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[8%] md:pt-[calc(9%-1.5cm)] lg:pt-[calc(8%-1.5cm)] pl-6 md:pl-[calc(1.5rem+1cm)] pr-4 pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left training-text-slide"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                    lineHeight: '1.5',
                  }}
                >
                  {heroClaimLine1.split('\n').map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 leading-relaxed text-sm md:text-base font-semibold mb-4">
              Ihr vertrauenswürdiger Partner für individuelles Training und strategisches Coaching in der DACH-Region. Wir begleiten ausländische Hersteller beim eigenständigen Markteintritt und sichern durch gezielten Wissenstransfer den Erfolg Ihrer Teams vor Ort.
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p3Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p3Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>

            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>

            {/* Widget 6 */}
            <p className="font-heading text-sm md:text-base text-primary-600 font-bold mt-8 md:mt-10 mb-3 md:mb-4">
              Warum Trainingskurse, Seminare und Business-Coaching?
            </p>

            {/* Widget 7 */}
            <p className="text-foreground-950 leading-relaxed text-sm md:text-base">
              Ihre Vision der Erschließung eines oder mehrerer neuer Märkte bekommt eine perfekt angepasste Struktur und eine umsetzbare Vorgehensweise. Dies sichert die Harmonie mit Ihrer gesamten Unternehmenskultur und stellt sicher, dass Ihr Auslandsprojekt nachhaltig zum Erfolg wird.
            </p>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}